import { memo, type PropsWithChildren } from "react";

import filters from "byzantine/src/filters";
import { Localized } from "@fluent/react";

import CardThumbnail from "../../../../../components/settings/cards/CardImage/CardThumbnail";
import styles from "../../SearchBar.module.scss";

import MultiRow from "./SearchBarResults.MultiRow";
import SingleRow from "./SearchBarResults.SingleRow";

// To-do: retrieve data from redux
// https://linear.app/narmi/issue/CDB-4769/fe-create-useglobalsearch-to-provide-search-results-to-component
const mockRecentSearches = [
  "venmo",
  "Netflix is a middling studio despite some decent movie successes. At least they've funded some great projects.",
  "Ralph",
  "Settings",
  "gold rewards",
];
const mockTransactions = [
  {
    description: "Check deposit from Ralph",
    settled: "2026-07-13T00:00:00-04:00",
    created: "2026-07-12T00:00:00-04:00",
    amount: 5000 as Cents,
  },
  {
    description:
      "Venmo transfer, which is a silly product considering that paypal exists AND they own it",
    settled: "",
    created: "2026-07-13T00:00:00-04:00",
    amount: -1500 as Cents,
  },
];
const mockAccounts = [
  {
    name: "Premium Checking",
    accountType: "Checking",
    balance: 125000 as Cents,
  },
  {
    name: "Gold Savings",
    accountType: "Savings",
    balance: 710000 as Cents,
  },
  {
    name: "Ultimate Rewards CC",
    accountType: "Credit card",
    balance: 62081 as Cents,
  },
];
const mockCards = [
  {
    name: "Credit card",
    lastFour: "3612",
    network: "Visa" as const,
    cardImage: `data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgd2lkdGg9IjE2NCIgaGVpZ2h0PSIxMDQiIHZpZXdCb3g9IjAgMCAxNjQgMTA0IiBmaWxsPSJub25lIj48cmVjdCB3aWR0aD0iMTY0IiBoZWlnaHQ9IjEwNCIgcng9IjEwIiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXIpIiAvPjxtYXNrIGlkPSJtYXNrMCIgbWFzay10eXBlPSJhbHBoYSIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgeD0iMCIgeT0iMCIgd2lkdGg9IjE2NCIgaGVpZ2h0PSIxMDQiPjxyZWN0IHdpZHRoPSIxNjQiIGhlaWdodD0iMTA0IiByeD0iMzAiIGZpbGw9IiNFRDcxM0MiIC8+PC9tYXNrPjxnIG1hc2s9InVybCgjbWFzazApIj48ZWxsaXBzZSBvcGFjaXR5PSIwLjEiIGN4PSIxMyIgY3k9IjExMSIgcng9Ijg0IiByeT0iNTkiIGZpbGw9ImJsYWNrIiAvPjxlbGxpcHNlIG9wYWNpdHk9IjAuMDgiIGN4PSIxNTIiIGN5PSItMTUiIHJ4PSI4NCIgcnk9IjU5IiBmaWxsPSJibGFjayIgLz48L2c+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJwYWludDBfbGluZWFyIiB4MT0iODIuNTY1NSIgeTE9Ii0xNS45MTU4IiB4Mj0iNzUuMzc0NCIgeTI9Ijk1LjE4MzYiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj48c3RvcCBzdG9wLWNvbG9yPSIjMUE0MzM4IiAvPjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iIzAwNjU1MyIgLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48L3N2Zz4=`,
  },
];
const mockPages = [
  {
    title: "Transfers",
    url: "/transfer",
  },
  {
    title: "Settings",
    url: "/settings",
  },
];

interface ResultsSectionProps extends PropsWithChildren {
  titleId: string;
}

const ResultsSection = memo(function ResultsSection({
  titleId,
  children,
}: ResultsSectionProps) {
  return (
    <div>
      <div className={styles["results-section-header"]}>
        <Localized id={titleId} />
      </div>
      <div className={styles["results-children"]}>{children}</div>
    </div>
  );
});

export const RecentSearchesResults = memo(function RecentSearchesResults() {
  if (!mockRecentSearches?.length) {
    return null;
  }
  return (
    <ResultsSection titleId={"search-bar-results-section-title-recents"}>
      {mockRecentSearches.map((recent) => (
        <SingleRow
          key={recent}
          title={recent}
          iconLeft="clock"
          iconRight="x"
          onClickRow={() => {}}
          onClickRight={() => {}}
        />
      ))}
    </ResultsSection>
  );
});

export const PagesResults = memo(function PagesResults() {
  return (
    <ResultsSection titleId={"search-bar-results-section-title-pages"}>
      {mockPages.map(({ title }) => (
        <SingleRow
          key={title}
          title={title}
          iconRight="chevron-right"
          onClickRow={() => {}}
        />
      ))}
    </ResultsSection>
  );
});

export const TransactionsResults = memo(function TransactionsResults() {
  return (
    <ResultsSection titleId={"search-bar-results-section-title-transactions"}>
      {mockTransactions.map(({ description, amount, created, settled }) => (
        <MultiRow
          key={description}
          title={description}
          subtitle={settled || created}
          amount={filters.currency(amount)}
          onClickRow={() => {}}
        />
      ))}
    </ResultsSection>
  );
});

export const AccountsResults = memo(function AccountsResults() {
  return (
    <ResultsSection titleId={"search-bar-results-section-title-accounts"}>
      {mockAccounts.map(({ name, balance, accountType }) => (
        <MultiRow
          key={name}
          title={name}
          subtitle={accountType}
          amount={filters.currency(balance)}
          onClickRow={() => {}}
        />
      ))}
    </ResultsSection>
  );
});

export const CardsResults = memo(function CardsResults() {
  return (
    <ResultsSection titleId={"search-bar-results-section-title-cards"}>
      {mockCards.map(({ name, lastFour, cardImage }) => (
        <MultiRow
          key={name}
          title={name}
          subtitle={lastFour}
          leftItem={
            <div style={{ width: "46px" }}>
              <CardThumbnail image={cardImage} size="x-small" />
            </div>
          }
          onClickRow={() => {}}
        />
      ))}
    </ResultsSection>
  );
});
